import { useQuery } from 'react-query'

import { request } from '../../global/api/legacyApi'
import { HTTPMethod } from '../utils/api.types'
import type { Response } from '../utils/api.types'

type GetLiveRealtimeRequest = {
  live_id: number
  presenter_token?: string
  token?: string
  channel_type?: 'public'
}

type LiveRealtimeDTO = {
  realtime_url: string
  realtime_channel_site: string
  realtime_channel?: string
}

type LiveRealtime = {
  realtimeURL: string
  publicChannelURL: string
  privateChannelURL: string | null
}

type GetLiveRealtimeResponse = Response & {
  data: LiveRealtimeDTO
}

type LiveRealtimeParams = {
  liveId: number
  channelType?: 'public'
  presenterToken?: string
  token?: string
}

type UseLiveRealtimeOptions = {
  liveId: number
  isEnabled?: boolean
} & (
  | {
      includePrivate?: false
      token: string
    }
  | {
      includePrivate: true
      presenterToken?: string
    }
)

const getLiveRealtime = (params: LiveRealtimeParams) =>
  request<GetLiveRealtimeResponse>({
    url: '/api/2/live/realtime',
    data: {
      live_id: params.liveId,
      presenter_token: params.presenterToken,
      token: params.token,
      channel_type: params.channelType,
    } satisfies GetLiveRealtimeRequest,
    method: HTTPMethod.GET,
  })

function maybeTrimAfterSecondSlashInclusive(text: string): string {
  const indexOfSecondSlash = text.indexOf('/', 1)

  if (indexOfSecondSlash === -1) {
    return text
  }

  return text.substring(0, indexOfSecondSlash)
}

const mapping = (response: GetLiveRealtimeResponse): LiveRealtime => {
  const privateChannelURL =
    response.data.realtime_channel !== undefined
      ? maybeTrimAfterSecondSlashInclusive(response.data.realtime_channel)
      : null

  return {
    realtimeURL: response.data.realtime_url,
    publicChannelURL: response.data.realtime_channel_site,
    privateChannelURL,
  }
}

const useLiveRealtimeQuery = (options: UseLiveRealtimeOptions) => {
  const { liveId, isEnabled } = options

  return useQuery(
    ['live', 'realtime', options],
    async () => {
      const params: LiveRealtimeParams = { liveId }

      if (options.includePrivate) {
        params.presenterToken = options.presenterToken
      } else {
        params.channelType = 'public'
        params.token = options.token
      }

      const response = await getLiveRealtime(params)

      return mapping(response)
    },
    {
      enabled: isEnabled,
      cacheTime: Infinity,
      staleTime: Infinity,
    }
  )
}

export default useLiveRealtimeQuery
